import React, { useCallback, useEffect, useState } from 'react';
import Layout from '../../shared/components/Layout';
import Modal from '../../shared/components/Modal';
import Pagination from '../../shared/components/Pagination';
import { useAuth } from '../auth/AuthContext';
import { extractErrorMessage } from '../../shared/utils/errors';
import { HotWallet, PaginatedResponse } from '../../shared/types';
import { validateTronAddressFormat } from '../../shared/utils/tronAddress';
import { validateBtcAddressFormat, validateEvmAddressFormat } from '../../shared/utils/walletAddress';
import {
  HotWalletFilters,
  createHotWallet,
  deleteHotWallet,
  listHotWallets,
  updateHotWallet,
} from './hotWalletApi';
import {
  formatCurrencyNetworkLabel,
  formatNetworkLabel,
  BTC_NETWORK_OPTION,
  ETH_NETWORK_OPTION,
  TRC20_NETWORK_OPTION,
  TRON_NETWORK_OPTION,
} from '../../shared/utils/networkLabel';

const CURRENCY_NETWORKS: Record<string, { value: string; label: string }[]> = {
  USDT: [TRC20_NETWORK_OPTION],
  ETH: [ETH_NETWORK_OPTION],
  BTC: [BTC_NETWORK_OPTION],
  TRX: [TRON_NETWORK_OPTION],
};
const CURRENCY_OPTIONS = Object.keys(CURRENCY_NETWORKS);
const NETWORK_OPTIONS = Object.values(CURRENCY_NETWORKS).flat();

const HEX_PRIVATE_KEY_RE = /^(0x)?[0-9a-fA-F]{64}$/;
// WIF (5…/K…/L… mainnet, 9…/c… testnet) is 51–52 Base58 characters.
const WIF_PRIVATE_KEY_RE = /^[1-9A-HJ-NP-Za-km-z]{51,52}$/;

const formatDateTime = (iso: string | null): string => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
};

const validatePrivateKey = (
  currency: string,
  network: string,
  value: string
): string | null => {
  if (!value.trim()) return 'Private key is required';
  if (
    (currency === 'USDT' && network === 'TRC20') ||
    (currency === 'ETH' && network === 'ETH') ||
    (currency === 'TRX' && network === 'TRON')
  ) {
    if (!HEX_PRIVATE_KEY_RE.test(value.trim())) {
      return `${currency === 'ETH' ? 'Ethereum' : 'TRON'} private key must be 64 hexadecimal characters`;
    }
  }
  if (currency === 'BTC' && network === 'BTC') {
    const trimmed = value.trim();
    if (!HEX_PRIVATE_KEY_RE.test(trimmed) && !WIF_PRIVATE_KEY_RE.test(trimmed)) {
      return 'Bitcoin private key must be WIF (5…/K…/L…) or 64 hexadecimal characters';
    }
  }
  return null;
};

const validateHotWalletAddress = (
  currency: string,
  network: string,
  value: string
): string | null => {
  if (currency === 'USDT' && network === 'TRC20') return validateTronAddressFormat(value);
  if (currency === 'ETH' && network === 'ETH') return validateEvmAddressFormat(value);
  if (currency === 'BTC' && network === 'BTC') return validateBtcAddressFormat(value);
  if (currency === 'TRX' && network === 'TRON') return validateTronAddressFormat(value);
  return null;
};

const addressPlaceholder = (currency: string): string => {
  if (currency === 'ETH') return '0x…';
  if (currency === 'BTC') return 'bc1… / 1… / 3…';
  return 'T…';
};

const privateKeyPlaceholder = (currency: string): string =>
  currency === 'BTC' ? 'WIF (5…/K…/L…) or 64 hex characters' : '64 hex characters';

const HotWalletsPage: React.FC = () => {
  const { admin } = useAuth();

  const canManage =
    admin?.role === 'super_admin' ||
    admin?.backoffice_role === 'administrator' ||
    (admin?.permissions?.includes('withdrawal.manage_hot_wallets') ?? false);

  const [wallets, setWallets] = useState<PaginatedResponse<HotWallet> | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState<HotWalletFilters>({});
  const [draftFilters, setDraftFilters] = useState<HotWalletFilters>({});

  const [createOpen, setCreateOpen] = useState(false);
  const [createCurrency, setCreateCurrency] = useState<string>('USDT');
  const [createNetwork, setCreateNetwork] = useState<string>('TRC20');
  const [createAddress, setCreateAddress] = useState('');
  const [createAddressError, setCreateAddressError] = useState<string | null>(null);
  const [createPrivateKey, setCreatePrivateKey] = useState('');
  const [createPrivateKeyError, setCreatePrivateKeyError] = useState<string | null>(null);
  const [createLabel, setCreateLabel] = useState('');
  const [createIsActive, setCreateIsActive] = useState(true);
  const [createSubmitting, setCreateSubmitting] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const [editTarget, setEditTarget] = useState<HotWallet | null>(null);
  const [editLabel, setEditLabel] = useState('');
  const [editIsActive, setEditIsActive] = useState(true);
  const [editRotateKey, setEditRotateKey] = useState(false);
  const [editPrivateKey, setEditPrivateKey] = useState('');
  const [editPrivateKeyError, setEditPrivateKeyError] = useState<string | null>(null);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  const [deleteTarget, setDeleteTarget] = useState<HotWallet | null>(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const load = useCallback(async (page: number, filtersToUse: HotWalletFilters) => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await listHotWallets({ ...filtersToUse, page, per_page: 20 });
      setWallets(data);
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to load hot wallets'));
      setWallets(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load(currentPage, filters);
  }, [currentPage, filters, load]);

  const applyFilters = () => {
    setFilters(draftFilters);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setDraftFilters({});
    setFilters({});
    setCurrentPage(1);
  };

  const openCreateModal = () => {
    setCreateCurrency('USDT');
    setCreateNetwork('TRC20');
    setCreateAddress('');
    setCreateAddressError(null);
    setCreatePrivateKey('');
    setCreatePrivateKeyError(null);
    setCreateLabel('');
    setCreateIsActive(true);
    setCreateError(null);
    setCreateOpen(true);
  };

  const onCreateCurrencyChange = (currency: string) => {
    const network = CURRENCY_NETWORKS[currency]?.[0]?.value ?? '';
    setCreateCurrency(currency);
    setCreateNetwork(network);
    setCreateAddressError(
      createAddress.trim() ? validateHotWalletAddress(currency, network, createAddress) : null
    );
    setCreatePrivateKeyError(
      createPrivateKey.trim() ? validatePrivateKey(currency, network, createPrivateKey) : null
    );
  };

  const onCreateAddressChange = (value: string) => {
    setCreateAddress(value);
    setCreateAddressError(
      value.trim() ? validateHotWalletAddress(createCurrency, createNetwork, value) : null
    );
  };

  const onCreatePrivateKeyChange = (value: string) => {
    setCreatePrivateKey(value);
    setCreatePrivateKeyError(
      value.trim() ? validatePrivateKey(createCurrency, createNetwork, value) : null
    );
  };

  const submitCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    const addressError = validateHotWalletAddress(createCurrency, createNetwork, createAddress);
    if (addressError) {
      setCreateAddressError(addressError);
      return;
    }
    const keyError = validatePrivateKey(createCurrency, createNetwork, createPrivateKey);
    if (keyError) {
      setCreatePrivateKeyError(keyError);
      return;
    }

    setCreateSubmitting(true);
    setCreateError(null);
    try {
      await createHotWallet({
        currency: createCurrency,
        network: createNetwork,
        address: createAddress.trim(),
        private_key: createPrivateKey.trim(),
        label: createLabel.trim() || null,
        is_active: createIsActive,
      });
      setCreateOpen(false);
      setCreatePrivateKey('');
      load(currentPage, filters);
    } catch (err) {
      setCreateError(extractErrorMessage(err, 'Failed to create hot wallet'));
    } finally {
      setCreateSubmitting(false);
    }
  };

  const openEditModal = (wallet: HotWallet) => {
    setEditTarget(wallet);
    setEditLabel(wallet.label ?? '');
    setEditIsActive(wallet.is_active);
    setEditRotateKey(false);
    setEditPrivateKey('');
    setEditPrivateKeyError(null);
    setEditError(null);
  };

  const closeEditModal = () => {
    setEditTarget(null);
    setEditError(null);
    setEditPrivateKey('');
  };

  const onEditPrivateKeyChange = (value: string) => {
    setEditPrivateKey(value);
    if (!editTarget) return;
    setEditPrivateKeyError(
      value.trim() ? validatePrivateKey(editTarget.currency, editTarget.network, value) : null
    );
  };

  const submitEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editTarget) return;

    if (editRotateKey) {
      const keyError = validatePrivateKey(editTarget.currency, editTarget.network, editPrivateKey);
      if (keyError) {
        setEditPrivateKeyError(keyError);
        return;
      }
    }

    setEditSubmitting(true);
    setEditError(null);
    try {
      const payload: Parameters<typeof updateHotWallet>[1] = {
        label: editLabel.trim() || null,
        is_active: editIsActive,
      };
      if (editRotateKey && editPrivateKey.trim()) {
        payload.private_key = editPrivateKey.trim();
      }
      await updateHotWallet(editTarget.id, payload);
      closeEditModal();
      load(currentPage, filters);
    } catch (err) {
      setEditError(extractErrorMessage(err, 'Failed to update hot wallet'));
    } finally {
      setEditSubmitting(false);
    }
  };

  const openDeleteModal = (wallet: HotWallet) => {
    setDeleteTarget(wallet);
    setDeleteError(null);
  };

  const closeDeleteModal = () => {
    setDeleteTarget(null);
    setDeleteError(null);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleteSubmitting(true);
    setDeleteError(null);
    try {
      await deleteHotWallet(deleteTarget.id);
      closeDeleteModal();
      load(currentPage, filters);
    } catch (err) {
      setDeleteError(extractErrorMessage(err, 'Failed to delete hot wallet'));
    } finally {
      setDeleteSubmitting(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white">Hot Wallets</h1>
            <p className="text-slate-400 mt-1 max-w-3xl">
              Platform-owned source addresses that pay outgoing withdrawals on each network. The
              private key is encrypted at rest and is never exposed back through this UI — once
              saved, it can only be rotated.
            </p>
          </div>
          {canManage && (
            <button type="button" onClick={openCreateModal} className="btn btn-primary shrink-0">
              Add Withdraw wallet
            </button>
          )}
        </div>

        <div className="card p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3 items-end">
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">Currency</label>
            <select
              value={draftFilters.currency ?? ''}
              onChange={(e) =>
                setDraftFilters((p) => ({ ...p, currency: e.target.value || undefined }))
              }
              className="input py-2"
            >
              <option value="">Any</option>
              {CURRENCY_OPTIONS.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">Network</label>
            <select
              value={draftFilters.network ?? ''}
              onChange={(e) =>
                setDraftFilters((p) => ({ ...p, network: e.target.value || undefined }))
              }
              className="input py-2"
            >
              <option value="">Any</option>
              {NETWORK_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">Address</label>
            <input
              type="text"
              value={draftFilters.address ?? ''}
              onChange={(e) =>
                setDraftFilters((p) => ({ ...p, address: e.target.value || undefined }))
              }
              className="input py-2 font-mono text-xs"
              placeholder="T… / 0x…"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">Status</label>
            <select
              value={draftFilters.is_active === undefined ? '' : String(draftFilters.is_active)}
              onChange={(e) => {
                const v = e.target.value;
                setDraftFilters((p) => ({
                  ...p,
                  is_active: v === '' ? undefined : v,
                }));
              }}
              className="input py-2"
            >
              <option value="">Any</option>
              <option value="true">Active</option>
              <option value="false">Disabled</option>
            </select>
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={applyFilters} className="btn btn-primary py-2 flex-1">
              Apply
            </button>
            <button type="button" onClick={clearFilters} className="btn btn-secondary py-2">
              Clear
            </button>
          </div>
        </div>

        <div className="card overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-12 h-12 border-4 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin" />
            </div>
          ) : error ? (
            <div className="p-6 text-center">
              <p className="text-red-400">{error}</p>
            </div>
          ) : !wallets?.data.length ? (
            <div className="p-12 text-center text-slate-400 space-y-3">
              <p>No hot wallets configured yet.</p>
              {canManage && (
                <button type="button" onClick={openCreateModal} className="btn btn-primary">
                  Add Withdraw wallet
                </button>
              )}
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-800/50">
                    <tr>
                      <th className="table-header">ID</th>
                      <th className="table-header">Currency</th>
                      <th className="table-header">Network</th>
                      <th className="table-header">Address</th>
                      <th className="table-header">Label</th>
                      <th className="table-header">Active</th>
                      <th className="table-header">Last used</th>
                      <th className="table-header">Created</th>
                      {canManage && <th className="table-header text-right">Actions</th>}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800">
                    {wallets.data.map((wallet) => (
                      <tr key={wallet.id} className="hover:bg-slate-800/30 transition-colors">
                        <td className="table-cell font-mono text-xs text-slate-500">#{wallet.id}</td>
                        <td className="table-cell text-white font-medium">{wallet.currency}</td>
                        <td className="table-cell text-slate-300">{formatNetworkLabel(wallet.network)}</td>
                        <td
                          className="table-cell font-mono text-xs text-slate-300 max-w-[240px] truncate"
                          title={wallet.address}
                        >
                          {wallet.address}
                        </td>
                        <td className="table-cell text-slate-300">{wallet.label || '—'}</td>
                        <td className="table-cell">
                          {wallet.is_active ? (
                            <span className="badge badge-success">Active</span>
                          ) : (
                            <span className="badge bg-slate-700/40 text-slate-300 border border-slate-600">
                              Disabled
                            </span>
                          )}
                        </td>
                        <td className="table-cell text-slate-400 text-sm whitespace-nowrap">
                          {formatDateTime(wallet.last_used_at)}
                        </td>
                        <td className="table-cell text-slate-400 text-sm whitespace-nowrap">
                          {formatDateTime(wallet.created_at)}
                        </td>
                        {canManage && (
                          <td className="table-cell text-right">
                            <div className="inline-flex gap-1 justify-end">
                              <button
                                type="button"
                                onClick={() => openEditModal(wallet)}
                                className="btn btn-secondary py-1 px-3 text-xs"
                              >
                                Edit
                              </button>
                              <button
                                type="button"
                                onClick={() => openDeleteModal(wallet)}
                                className="btn btn-secondary py-1 px-3 text-xs text-red-400 hover:text-red-300"
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {wallets.last_page > 1 && (
                <div className="p-4 border-t border-slate-800 flex items-center justify-between gap-4">
                  <p className="text-xs text-slate-500">
                    {wallets.total} hot wallet{wallets.total === 1 ? '' : 's'}
                  </p>
                  <Pagination
                    currentPage={wallets.current_page}
                    lastPage={wallets.last_page}
                    onPageChange={setCurrentPage}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <Modal
        isOpen={createOpen}
        onClose={() => !createSubmitting && setCreateOpen(false)}
        title="Add hot wallet"
        size="md"
      >
        <form onSubmit={submitCreate} className="space-y-4">
          <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg text-amber-300 text-xs leading-relaxed">
            The private key is encrypted at rest and never returned by the API. Once you save it
            here you cannot read it back — only rotate it. Make sure you have an offline backup
            before submitting.
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1">Currency</label>
              <select
                value={createCurrency}
                onChange={(e) => onCreateCurrencyChange(e.target.value)}
                className="input py-2"
              >
                {CURRENCY_OPTIONS.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1">Network</label>
              <select
                value={createNetwork}
                onChange={(e) => setCreateNetwork(e.target.value)}
                className="input py-2"
              >
                {(CURRENCY_NETWORKS[createCurrency] ?? NETWORK_OPTIONS).map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">Address</label>
            <input
              type="text"
              value={createAddress}
              onChange={(e) => onCreateAddressChange(e.target.value)}
              className={`input py-2 font-mono text-xs ${createAddressError ? 'border-red-500/60' : ''}`}
              placeholder={addressPlaceholder(createCurrency)}
              autoComplete="off"
              spellCheck={false}
            />
            {createAddressError && (
              <p className="text-xs text-red-400 mt-1">{createAddressError}</p>
            )}
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">Private key</label>
            <input
              type="password"
              value={createPrivateKey}
              onChange={(e) => onCreatePrivateKeyChange(e.target.value)}
              className={`input py-2 font-mono text-xs ${createPrivateKeyError ? 'border-red-500/60' : ''}`}
              placeholder={privateKeyPlaceholder(createCurrency)}
              autoComplete="off"
              spellCheck={false}
            />
            {createPrivateKeyError && (
              <p className="text-xs text-red-400 mt-1">{createPrivateKeyError}</p>
            )}
            <p className="text-[11px] text-slate-500 mt-1">
              Stored encrypted with the platform's app key. Never visible after submit.
            </p>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">Label (optional)</label>
            <input
              type="text"
              value={createLabel}
              onChange={(e) => setCreateLabel(e.target.value)}
              className="input py-2"
              placeholder="e.g. Primary USDT TRC20 / Primary ETH / Primary BTC"
              maxLength={100}
            />
          </div>

          <label className="inline-flex items-center gap-2 text-sm text-slate-300">
            <input
              type="checkbox"
              checked={createIsActive}
              onChange={(e) => setCreateIsActive(e.target.checked)}
              className="rounded border-slate-700 bg-slate-800 text-indigo-500"
            />
            Active — eligible to broadcast withdrawals
          </label>

          {createError && (
            <p className="text-sm text-red-400">{createError}</p>
          )}

          <div className="flex gap-2 justify-end pt-2">
            <button
              type="button"
              onClick={() => setCreateOpen(false)}
              className="btn btn-secondary"
              disabled={createSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createSubmitting || !createAddress.trim() || !createPrivateKey.trim()}
              className="btn btn-primary disabled:opacity-60"
            >
              {createSubmitting ? 'Saving…' : 'Save hot wallet'}
            </button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={editTarget !== null}
        onClose={closeEditModal}
        title="Edit hot wallet"
        size="md"
      >
        {editTarget && (
          <form onSubmit={submitEdit} className="space-y-4">
            <div className="card p-3 bg-slate-800/40 text-xs space-y-1">
              <p>
                <span className="text-slate-500">ID:</span>{' '}
                <span className="font-mono text-slate-200">#{editTarget.id}</span>
              </p>
              <p>
                <span className="text-slate-500">Pair:</span>{' '}
                <span className="text-slate-200">
                  {formatCurrencyNetworkLabel(editTarget.currency, editTarget.network)}
                </span>
              </p>
              <p>
                <span className="text-slate-500">Address:</span>{' '}
                <span className="font-mono text-slate-200 break-all">{editTarget.address}</span>
              </p>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1">Label</label>
              <input
                type="text"
                value={editLabel}
                onChange={(e) => setEditLabel(e.target.value)}
                className="input py-2"
                maxLength={100}
              />
            </div>

            <label className="inline-flex items-center gap-2 text-sm text-slate-300">
              <input
                type="checkbox"
                checked={editIsActive}
                onChange={(e) => setEditIsActive(e.target.checked)}
                className="rounded border-slate-700 bg-slate-800 text-indigo-500"
              />
              Active — eligible to broadcast withdrawals
            </label>

            <div className="border-t border-slate-800 pt-4 space-y-3">
              <label className="inline-flex items-center gap-2 text-sm text-slate-200">
                <input
                  type="checkbox"
                  checked={editRotateKey}
                  onChange={(e) => {
                    setEditRotateKey(e.target.checked);
                    if (!e.target.checked) {
                      setEditPrivateKey('');
                      setEditPrivateKeyError(null);
                    }
                  }}
                  className="rounded border-slate-700 bg-slate-800 text-amber-500"
                />
                Rotate private key
              </label>
              {editRotateKey && (
                <div>
                  <input
                    type="password"
                    value={editPrivateKey}
                    onChange={(e) => onEditPrivateKeyChange(e.target.value)}
                    className={`input py-2 font-mono text-xs ${editPrivateKeyError ? 'border-red-500/60' : ''}`}
                    placeholder="New private key"
                    autoComplete="off"
                    spellCheck={false}
                  />
                  {editPrivateKeyError && (
                    <p className="text-xs text-red-400 mt-1">{editPrivateKeyError}</p>
                  )}
                  <p className="text-[11px] text-amber-400/80 mt-1">
                    The current key will be replaced. Make sure the new key controls the wallet's
                    address.
                  </p>
                </div>
              )}
            </div>

            {editError && <p className="text-sm text-red-400">{editError}</p>}

            <div className="flex gap-2 justify-end pt-2">
              <button
                type="button"
                onClick={closeEditModal}
                className="btn btn-secondary"
                disabled={editSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={editSubmitting}
                className="btn btn-primary disabled:opacity-60"
              >
                {editSubmitting ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </form>
        )}
      </Modal>

      <Modal
        isOpen={deleteTarget !== null}
        onClose={closeDeleteModal}
        title="Delete hot wallet?"
        size="md"
      >
        {deleteTarget && (
          <div className="space-y-4">
            <p className="text-sm text-slate-300">
              This will permanently remove the hot wallet from the platform. New withdrawals on
              <span className="font-mono text-slate-200">
                {' '}
                {formatCurrencyNetworkLabel(deleteTarget.currency, deleteTarget.network)}
              </span>{' '}
              will fail until another active hot wallet is configured.
            </p>
            <div className="card p-3 bg-slate-800/40 text-xs space-y-1">
              <p>
                <span className="text-slate-500">ID:</span>{' '}
                <span className="font-mono text-slate-200">#{deleteTarget.id}</span>
              </p>
              <p>
                <span className="text-slate-500">Address:</span>{' '}
                <span className="font-mono text-slate-200 break-all">{deleteTarget.address}</span>
              </p>
            </div>

            {deleteError && <p className="text-sm text-red-400">{deleteError}</p>}

            <div className="flex gap-2 justify-end pt-2">
              <button
                type="button"
                onClick={closeDeleteModal}
                disabled={deleteSubmitting}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                disabled={deleteSubmitting}
                className="btn bg-red-600 hover:bg-red-500 text-white disabled:opacity-60"
              >
                {deleteSubmitting ? 'Deleting…' : 'Delete hot wallet'}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </Layout>
  );
};

export default HotWalletsPage;
